import { getAdjustmentType } from "../../../../utils/static/detailsPL";
import api from "../../../../api/api";

const LINKED_ROW_SOURCE_TYPES = ["p_l", "pl_schedule"];

export function canUpdateLinkedRow(source) {
  return Boolean(
    source?.sourceId &&
      LINKED_ROW_SOURCE_TYPES.includes(source.sourceType) &&
      source.row?.row_id &&
      source.row.schedule?.sch_id &&
      source.row.schedule.scheduleType
  );
}

export function getLinkedSnapshot(row) {
  const amount = Number(row?.amount);
  return {
    name: row?.name || "",
    amount: Number.isFinite(amount) ? amount : 0,
  };
}

export function markRowSynced(row) {
  if (!row?.schedule) return row;
  return {
    ...row,
    schedule: {
      ...row.schedule,
      lastSynced: getLinkedSnapshot(row),
    },
  };
}

export function markRowsSynced(rows, rowIds) {
  const ids = new Set(rowIds);
  return (rows || []).map((row) => (ids.has(row.row_id) ? markRowSynced(row) : row));
}

export function hydrateLinkedLastSynced(row) {
  if (!row?.schedule?.sch_id) return row;
  if (row.schedule.lastSynced) return row;
  return markRowSynced(row);
}

export function hydrateLinkedRows(rows) {
  return (rows || []).map(hydrateLinkedLastSynced);
}

export function isLinkedRowDirty(row) {
  if (!row?.schedule?.sch_id) return false;
  const synced = row.schedule.lastSynced;
  if (!synced) return false;
  const current = getLinkedSnapshot(row);
  const syncedAmount = Number(synced.amount);
  const lastAmount = Number.isFinite(syncedAmount) ? syncedAmount : 0;
  return current.name !== (synced.name || "") || current.amount !== lastAmount;
}

export function collectDirtyLinkedSources(
  rowsBySection,
  { sourceType, sourceId }
) {
  const sources = [];
  Object.entries(rowsBySection || {}).forEach(([section, rows]) => {
    (rows || []).forEach((row, rowIndex) => {
      const source = { row, sourceType, sourceId, section, rowIndex };
      if (canUpdateLinkedRow(source) && isLinkedRowDirty(row)) {
        sources.push(source);
      }
    });
  });
  return sources;
}

export function getLinkedRowUpdateKey(source, tcId) {
  if (!canUpdateLinkedRow(source)) return null;
  return JSON.stringify([
    tcId,
    source.sourceType,
    source.sourceId,
    source.row.row_id,
    getScheduleDocumentType(source.row.schedule.scheduleType),
    source.row.schedule.sch_id,
  ]);
}

// Update an existing link by source-row identity, without unlinking or rebuilding
// the target schedule row. The server must retain its row type and custom fields.
// Keep the signed source amount; target amount/cost conversion belongs to the server.
export function buildLinkedRowUpdatePayload(source, tcId) {
  if (!tcId || !canUpdateLinkedRow(source)) {
    throw new Error("This row must be linked to a schedule before updating.");
  }
  const amount = Number(source.row.amount);
  if (!Number.isFinite(amount)) {
    throw new Error("Please enter a valid amount before updating the linked schedule.");
  }
  return {
    tc_id: tcId,
    sourceType: source.sourceType,
    source_id: source.sourceId,
    refDoc: getScheduleDocumentType(source.row.schedule.scheduleType),
    sch_id: source.row.schedule.sch_id,
    source_row: {
      row_id: source.row.row_id,
      name: source.row.name || "",
      amount,
    },
    source: { section: source.section, index: source.rowIndex },
  };
}

async function requestLinkedRowUpdate(payload) {
  // api interceptor already unwraps axios to the envelope: { status, message, data }.
  return api.post("/docs/updateLinkedAutoloadData", payload);
}

export function buildLinkedRowsUpdatePayload(sources, tcId) {
  if (!sources?.length) {
    throw new Error("No linked rows to update.");
  }
  const first = buildLinkedRowUpdatePayload(sources[0], tcId);
  return {
    tc_id: tcId,
    sourceType: first.sourceType,
    source_id: first.source_id,
    rows: sources.map((source) => {
      const item = buildLinkedRowUpdatePayload(source, tcId);
      return {
        refDoc: item.refDoc,
        sch_id: item.sch_id,
        source_row: item.source_row,
        source: item.source,
      };
    }),
  };
}

function assertLinkedUpdateSucceeded(response) {
  if (response?.status !== "success" || !response.data?.updated) {
    throw new Error(response?.message || "Unable to update the linked schedule.");
  }
  return response;
}

export function getScheduleDocumentType(scheduleType) {
  return scheduleType === "FAAdditions" ? "ca_schedule" : "pl_schedule";
}

// Return a new row so API completion never mutates the page's saved snapshot.
export function createTaggedRow(row, schedule, reference, companyType) {
  return {
    ...row,
    schedule: {
      ...schedule,
      scheduleType: reference.scheduleType,
      lastSynced: getLinkedSnapshot(row),
      adjustment:
        companyType === "investment"
          ? "none"
          : getAdjustmentType(reference.scheduleType, reference.adjustment),
      source: reference.refSource,
    },
  };
}

/**
 * API operations shared by P&L and breakdown sources. Linking dialogs, source
 * saves and navigation remain owned by callers. Source-data updates use their
 * own shared payload and operation, rather than a side effect of linking/saving.
 */
export function createScheduleTaggingService({
  tcId,
  companyType,
  addAutoloadData,
  deleteAutoloadData,
  createPLSchedule,
  createCASchedule,
  updateLinkedAutoloadData = requestLinkedRowUpdate,
}) {
  return {
    canUpdateLinkedRow,

    async updateLinkedRow(source) {
      const payload = buildLinkedRowUpdatePayload(source, tcId);
      return assertLinkedUpdateSucceeded(await updateLinkedAutoloadData(payload));
    },

    async updateLinkedRows(sources) {
      const valid = (sources || []).filter((source) => canUpdateLinkedRow(source));
      if (valid.length === 0) {
        throw new Error("No linked rows to update.");
      }
      if (valid.length === 1) {
        return assertLinkedUpdateSucceeded(
          await updateLinkedAutoloadData(buildLinkedRowUpdatePayload(valid[0], tcId))
        );
      }

      const groups = new Map();
      valid.forEach((source) => {
        const key = `${source.sourceType}:${source.sourceId}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(source);
      });

      let lastResponse = null;
      for (const group of groups.values()) {
        lastResponse = assertLinkedUpdateSucceeded(
          await updateLinkedAutoloadData(buildLinkedRowsUpdatePayload(group, tcId))
        );
      }
      return lastResponse;
    },

    async unTagSchedule(sourceType, rowId, targetDocType) {
      return deleteAutoloadData({
        tc_id: tcId,
        source_doc_type: sourceType,
        ref_id: rowId,
        ref_doc_type: targetDocType,
      });
    },

    async tagSchedule(data, reference) {
      const response = await addAutoloadData(data);
      if (!response) {
        throw new Error("Unknown error happens during processing your request.");
      }
      return createTaggedRow(
        reference.row,
        reference.schedule,
        reference,
        companyType
      );
    },

    async createTagSchedule(data, reference) {
      const createSchedule =
        getScheduleDocumentType(reference.scheduleType) === "ca_schedule"
          ? createCASchedule
          : createPLSchedule;
      const response = await createSchedule(data);
      if (response?.status !== "success" || !response.data) {
        throw new Error("Unknown error happens during processing your request.");
      }
      const schedule = response.data;
      return createTaggedRow(
        reference.row,
        {
          sch_id: schedule.sch_id,
          scheduleTitle: schedule.schedule_title,
          scheduleNo: schedule.schedule_no,
        },
        reference,
        companyType
      );
    },
  };
}
